import {
  CellClassRules,
  ColDef,
  ColGroupDef,
  ExcelExportParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { get, set } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';

import { CompanyCellRenderer } from '../../../components/grid-renderers/CompanyCellRenderer';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { fundFilterStateAtom } from '../../../components/Layout/Navbar/Navigation/PortfolioReporting/FundNavigation/FundFilterSingleSelect';
import { KpiSection } from '../../../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { field2ToField3, IField } from '../../../data-models/field2.data-model';
import { field3ToColumnDef } from '../../../data-models/field3.data-model';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { getMultiPeriodReport } from '../../../services/queries/MaggieMultiPeriodReportingQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import {
  customFieldsByIdMapAtom,
  customFieldsByIdMapLoadable,
} from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import {
  getKpiFieldKey,
  kpiConfigByIdMapAtom,
  kpiConfigByKeyMapAtom,
} from '../../../services/state/KPIConfigState';
import { ColumnMeta } from '../../../types';
import { useAsync } from '../../../util/hook-utils';
import { getTypedFinancials, TypedFinancials } from '../../CompanyProfiles/utils/getTypedFinancials';
import { getValueFormatter } from '../components/OverviewTable/columns/ColumnFormatter';
import { getTypeBasedConfigs } from '../components/OverviewTable/columns/TypeBasedConfigs/TypeBasedConfigs';
import { columnsByPathState, columnsByPathStateLoadable, selectedViewIdPFJ } from '../state/ViewStateJ';
import {
  createMprViewSettings,
  IMprViewSettings,
  IMultiPeriodReportingDataPoint,
  IMultiPeriodReportingRequest,
  IMultiPeriodReportResponse,
  MprColumnConfig,
  MprLayout,
} from './MultiPeriodReporting.schemas';
import {
  formatMprItemHeader,
  getColumnConfigKey,
  getDateForConfig,
  getMprItemDisplayName,
  getMprItemKey,
  getMprPeriodHeader,
  getMprPeriodKey,
  getMprViewColumns,
  IMprCustomFieldColumnConfig,
  IMprKpiColumnConfig,
  IMprMetricsColumnConfig,
  isMprCustomFieldColumnConfig,
  isMprKpiColumnConfig,
  isMprKpiItem,
  isMprMetricsColumnConfig,
  isMprMetricsItem,
  MprItem,
  MprPeriod,
  mprCustomFieldColumnConfigsToDataPoints,
  mprKpiColumnConfigToDataPoint,
  mprMetricsColumnConfigsToDataPoints,
  mprChronologicalComparator,
  splitMprColumnConfig,
  uniqueMprItems,
  uniqueMprPeriods,
} from './multiPeriodReportingUtils';
import { kpiDefinitionByKeyMapAtom } from '../../../services/state/KPIConfigState';
import { FieldDefinition } from '../../../data-models/field-definition/field-definition.schema';
import { ExcelFormatterIdPrefix } from '../../../util/excel-formatter-service';
import { savedMprViewConfigAtom } from './useMprColumnConfigs';

export type MultiPeriodReportingDataByKpiId = Record<
  string,
  Record<string, Record<string, Record<KpiSection, TypedFinancials>>>
>;

export function getMultiPeriodReportingKpiDataMap(
  report: IMultiPeriodReportResponse,
  kpiConfigMap: Map<number, IField<unknown>>
) {
  return getTypedFinancials(report.data, kpiConfigMap).reduce(
    (acc, item) => {
      const kpiKey = getKpiFieldKey(kpiConfigMap.get(item.kpiId));
      if (!kpiKey) {
        return acc;
      }
      return set(acc, [item.companyId, kpiKey, item.date, item.period!, item.section!], item);
    },
    {} as Record<number, MultiPeriodReportingDataByKpiId>
  );
}

export function getMultiPeriodReportingMetricsDataMap(report: IMultiPeriodReportResponse) {
  return report.metricsData.reduce(
    (metricsByCompanyIdAndDate, metrics) => {
      const { companyId, date, ...paths } = metrics;
      Object.entries(paths).forEach(([path, value]) => {
        set(metricsByCompanyIdAndDate, [companyId, date, path], value);
      });
      return metricsByCompanyIdAndDate;
    },
    {} as Record<number, Record<string, Partial<IMetricsDataModel>>>
  );
}

export function getMultiPeriodReportingCustomFieldDataMap(report: IMultiPeriodReportResponse) {
  return report.customFieldData.reduce(
    (acc, customFieldData) => {
      const { entityId: companyId, customFieldId, date, value } = customFieldData;
      set(acc, [companyId, customFieldId, date], value);
      return acc;
    },
    {} as Record<number, Record<number, Record<string, unknown>>>
  );
}

export const MprCompanyColId = 'company';
const companyColDef: ColDef = {
  field: 'company',
  colId: MprCompanyColId,
  cellRenderer: CompanyCellRenderer,
  cellRendererParams: {
    linkToProfile: true,
  },
  comparator: (valueA, valueB, nodeA, nodeB) => {
    return (nodeA?.data?.company.name.toLocaleLowerCase() ?? '').localeCompare(
      nodeB?.data?.company.name.toLocaleLowerCase() ?? ''
    );
  },
  valueFormatter: (params: ValueFormatterParams) => {
    return params?.value?.name ?? '';
  },
  filter: 'agSetColumnFilter',
  filterParams: {
    buttons: ['reset'],
  },
  pinned: 'left',
  width: 200,
};

export function useMultiPeriodReportingColumnDefs() {
  const viewId = useAtomValue(selectedViewIdPFJ);
  const viewSettings = useAtomValue(savedMprViewConfigAtom(viewId));
  const settingsToColDefs = useMprColDefs();

  return useMemo(
    () => settingsToColDefs(viewSettings ?? createMprViewSettings()),
    [settingsToColDefs, viewSettings]
  );
}

/** The grid's column ids for a view's settings, company first, in display order. */
export function getMprColumnIds(settings: IMprViewSettings): string[] {
  if (settings.layout === MprLayout.rows) {
    return [MprCompanyColId, MprItemColId, ...rowsLayoutPeriods(settings).map(periodColId)];
  }
  return [MprCompanyColId, ...getMprViewColumns(settings).map((column) => getColumnConfigKey(column))];
}

export type MprColDef = ColDef | ColGroupDef;

export interface IMprCatalogs {
  kpiConfigMap: Map<string, IField<unknown>>;
  kpiDefinitionsByKey: Map<string, FieldDefinition>;
  columnsByPath: Map<string, ColumnMeta>;
  customFieldsById: Map<number, IField<unknown>>;
}

/** Every column definition for a view's settings, company column included. */
export function useMprColDefs() {
  const kpiConfigMap = useAtomValue(kpiConfigByKeyMapAtom);
  const kpiDefinitionsByKey = useAtomValue(kpiDefinitionByKeyMapAtom);
  const columnsByPath = useAtomValue(columnsByPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  const customFieldsById = useAtomValue(customFieldsByIdMapAtom);
  return useCallback(
    (settings: IMprViewSettings): MprColDef[] => {
      if (!columnsByPath || !customFieldsById) {
        return [];
      }
      const catalogs: IMprCatalogs = { kpiConfigMap, kpiDefinitionsByKey, columnsByPath, customFieldsById };
      return settings.layout === MprLayout.rows
        ? mprRowsLayoutColDefs(settings, catalogs)
        : mprColumnLayoutColDefs(settings, catalogs);
    },
    [columnsByPath, customFieldsById, kpiConfigMap, kpiDefinitionsByKey]
  );
}

/** The column for one item in one period, or null when the catalogs no longer know the item. */
export function mprConfigToColDef(
  config: MprColumnConfig,
  catalogs: IMprCatalogs
): ColDefWithSortOrder | null {
  if (isMprKpiColumnConfig(config)) {
    return kpiConfigToColumnDef(config, catalogs.kpiConfigMap);
  }
  if (isMprMetricsColumnConfig(config)) {
    return metricsConfigToColumnDef(config, catalogs.columnsByPath);
  }
  return customFieldConfigToColumnDef(config, catalogs.customFieldsById);
}

function mprItemName(item: MprItem, catalogs: IMprCatalogs) {
  const name = getMprItemDisplayName(
    item,
    catalogs.kpiDefinitionsByKey,
    catalogs.columnsByPath,
    catalogs.customFieldsById
  );
  return formatMprItemHeader(name, item);
}

/**
 * The column layouts as two-level headers: a group per period holding one column per item, or a
 * group per item holding one column per period. The leaf columns keep the ids of getMprColumnIds.
 */
export function mprColumnLayoutColDefs(settings: IMprViewSettings, catalogs: IMprCatalogs): MprColDef[] {
  const byItem = settings.layout === MprLayout.item;
  // each header once, not once per items × periods leaf
  const itemHeaders = new Map(
    uniqueMprItems(settings.items).map((item) => [getMprItemKey(item), mprItemName(item, catalogs)])
  );
  const periodHeaders = new Map(
    uniqueMprPeriods(settings.periods).map((period) => [getMprPeriodKey(period), getMprPeriodHeader(period)])
  );
  const groups = new Map<string, ColGroupDef>();
  for (const config of getMprViewColumns(settings)) {
    const def = mprConfigToColDef(config, catalogs);
    if (!def) {
      continue;
    }
    const { item, period } = splitMprColumnConfig(config);
    const itemKey = getMprItemKey(item);
    const periodKey = getMprPeriodKey(period);
    const itemHeader = itemHeaders.get(itemKey) ?? '';
    const periodHeader = periodHeaders.get(periodKey) ?? '';
    const groupId = byItem ? `item:${itemKey}` : `period:${periodKey}`;
    let group = groups.get(groupId);
    if (!group) {
      group = { groupId, headerName: byItem ? itemHeader : periodHeader, marryChildren: true, children: [] };
      groups.set(groupId, group);
    }
    group.children.push({ ...def, headerName: byItem ? periodHeader : itemHeader });
  }
  return [companyColDef, ...groups.values()];
}

// ---------- rows layout (FD-5793): one row per company and item, one column per period ----------

export const MprItemColId = 'item';

export interface IMprGridRow {
  company: ICompanyDataModel;
  /** Set in the rows layout only. */
  item?: MprItem;
}

export function getMprRowId(row: IMprGridRow): string {
  return row.item ? `${row.company.id}|${getMprItemKey(row.item)}` : String(row.company.id);
}

function rowsLayoutPeriods(settings: IMprViewSettings): MprPeriod[] {
  return uniqueMprPeriods(settings.periods).toSorted(mprChronologicalComparator);
}
function periodColId(period: MprPeriod) {
  return `period:${getMprPeriodKey(period)}`;
}

/** How one item's values display: the same definition its wide-layout column would use, minus the period. */
function itemColumnDef(item: MprItem, catalogs: IMprCatalogs): Partial<ColDef> | null {
  if (isMprKpiItem(item)) {
    const kpi = catalogs.kpiConfigMap.get(item.kpiKey);
    return kpi ? field3ToColumnDef(field2ToField3(kpi)) : null;
  }
  if (isMprMetricsItem(item)) {
    const columnMeta = catalogs.columnsByPath.get(item.columnPath);
    return columnMeta
      ? { ...getTypeBasedConfigs(columnMeta), valueFormatter: getValueFormatter(columnMeta) }
      : null;
  }
  const customField = catalogs.customFieldsById.get(item.customFieldId);
  return customField ? field3ToColumnDef(field2ToField3(customField)) : null;
}

function itemValueForPeriod(params: ValueGetterParams<IMprGridRow>, period: MprPeriod) {
  const row = params.data;
  if (!row?.item) {
    return undefined;
  }
  const date = getDateForConfig(period);
  if (isMprKpiItem(row.item)) {
    return get(params.context.kpiRowDataMap, [
      row.company.id,
      row.item.kpiKey,
      date,
      period.period,
      row.item.section,
    ])?.value;
  }
  if (isMprMetricsItem(row.item)) {
    return get(params.context.metricsDataMap, [row.company.id, date, row.item.columnPath]);
  }
  return get(params.context.customFieldDataMap, [row.company.id, row.item.customFieldId, date]);
}

export function mprRowsLayoutColDefs(settings: IMprViewSettings, catalogs: IMprCatalogs): ColDef[] {
  const items = resolvableMprItems(uniqueMprItems(settings.items), catalogs);
  const defsByItem = new Map(items.map((item) => [getMprItemKey(item), itemColumnDef(item, catalogs)]));
  const defFor = (row?: IMprGridRow) => (row?.item ? defsByItem.get(getMprItemKey(row.item)) : null) ?? null;
  // Excel styles are applied per cell from the class rules, so every style any item carries (in its
  // cellClass, or in the per-magnitude rules of a compact format) becomes a rule that holds on that
  // item's rows. The export reads the divisor from the row's own definition (withMprItemExport).
  const isExcelStyleId = (value: unknown): value is string =>
    typeof value === 'string' && value.startsWith(ExcelFormatterIdPrefix);
  const excelStyleRules: CellClassRules<IMprGridRow> = {};
  for (const def of defsByItem.values()) {
    const styleIds = [
      ...(Array.isArray(def?.cellClass) ? def.cellClass : []),
      ...Object.keys(def?.cellClassRules ?? {}),
    ].filter(isExcelStyleId);
    for (const styleId of styleIds) {
      if (styleId in excelStyleRules) {
        continue;
      }
      excelStyleRules[styleId] = (params) => {
        const rowDef = defFor(params.data);
        if (Array.isArray(rowDef?.cellClass) && rowDef.cellClass.includes(styleId)) {
          return true;
        }
        const rule = rowDef?.cellClassRules?.[styleId];
        return typeof rule === 'function' ? Boolean(rule(params)) : false;
      };
    }
  }
  const itemColDef: ColDef<IMprGridRow> = {
    colId: MprItemColId,
    headerName: 'Item',
    valueGetter: (params) => (params.data?.item ? mprItemName(params.data.item, catalogs) : ''),
    filter: 'agSetColumnFilter',
    filterParams: { buttons: ['reset'] },
    enableRowGroup: false,
    pinned: 'left',
    width: 220,
  };

  // A period column holds every item's values, so it keeps the grid's default filter and sort
  // rather than one item's numeric traits; sorting it compares across items.
  const periodColDefs = rowsLayoutPeriods(settings).map((period): ColDef<IMprGridRow> => ({
    colId: periodColId(period),
    headerName: getMprPeriodHeader(period),
    enableRowGroup: false,
    context: { mprItemDef: defFor } satisfies IMprPeriodColumnContext,
    valueGetter: (params) => itemValueForPeriod(params, period),
    // the row decides the format: each row is a different item
    valueFormatter: (params) => {
      const formatter = defFor(params.data)?.valueFormatter;
      return typeof formatter === 'function' ? formatter(params) : (params.value ?? '');
    },
    cellClass: (params) => {
      const cellClass = defFor(params.data)?.cellClass;
      return typeof cellClass === 'function' ? cellClass(params) : cellClass;
    },
    cellClassRules: excelStyleRules,
  }));

  return [companyColDef, itemColDef, ...periodColDefs];
}

interface IMprPeriodColumnContext {
  /** The column definition the row's item would have on its own; the export reads its Excel style and divisor. */
  mprItemDef: (row?: IMprGridRow) => Partial<ColDef> | null;
}

/** Items the catalogs know; a KPI, metric or custom field deleted since the view was saved has no row. */
export function resolvableMprItems(items: MprItem[], catalogs: IMprCatalogs): MprItem[] {
  return items.filter((item) =>
    isMprKpiItem(item)
      ? catalogs.kpiConfigMap.has(item.kpiKey)
      : isMprMetricsItem(item)
        ? catalogs.columnsByPath.has(item.columnPath)
        : catalogs.customFieldsById.has(item.customFieldId)
  );
}

/**
 * Wraps an export cell callback so a rows-layout period cell is processed with its row item's own
 * column definition: the shared export reads the Excel style and the value divisor from the column
 * definition, and in this layout those belong to the row, not the column.
 */
type ProcessCell = NonNullable<ExcelExportParams['processCellCallback']>;
export function withMprItemExport(process: ProcessCell): ProcessCell {
  return (params) => {
    const context = params.column.getColDef().context as Partial<IMprPeriodColumnContext> | undefined;
    const itemDef = context?.mprItemDef?.(params.node?.data as IMprGridRow | undefined);
    if (!itemDef) {
      return process(params);
    }
    const column = Object.create(params.column, { getColDef: { value: () => itemDef } });
    return process({ ...params, column });
  };
}

interface ColDefWithSortOrder extends ColDef {
  sortOrder: number;
}

export function metricsConfigToColumnDef(
  config: IMprMetricsColumnConfig,
  columnsByPath: Map<string, ColumnMeta>
): ColDefWithSortOrder | null {
  const columnMeta = columnsByPath.get(config.columnPath);
  if (!columnMeta) {
    return null;
  }
  const valueGetter = (params: ValueGetterParams) => {
    return get(params.context.metricsDataMap, [
      params.data.company.id,
      getDateForConfig(config),
      config.columnPath,
    ]);
  };
  const partialDefs = getTypeBasedConfigs(columnMeta, valueGetter);
  return {
    ...partialDefs,
    colId: getColumnConfigKey(config),
    enableRowGroup: false,
    sortOrder: config.sortOrder ?? Number.MAX_SAFE_INTEGER,
    valueGetter,
    valueFormatter: getValueFormatter(columnMeta),
  };
}

export function kpiConfigToColumnDef(
  config: IMprKpiColumnConfig,
  kpiMap: Map<string, IField<unknown>>
): ColDefWithSortOrder | null {
  const kpi = kpiMap.get(config.kpiKey!);
  if (!kpi) {
    return null;
  }
  return {
    ...field3ToColumnDef(field2ToField3(kpi)),
    colId: getColumnConfigKey(config),
    valueGetter: (params: ValueGetterParams) => {
      const dataMap = params.context.kpiRowDataMap as Record<number, MultiPeriodReportingDataByKpiId>;
      return get(dataMap, [
        params.data.company.id,
        config.kpiKey!,
        getDateForConfig(config),
        config.period!,
        config.section!,
      ])?.value;
    },
    sortOrder: config.sortOrder ?? Number.MAX_SAFE_INTEGER,
  };
}

export function customFieldConfigToColumnDef(
  config: IMprCustomFieldColumnConfig,
  customFieldsById: Map<number, IField<unknown>>
): ColDefWithSortOrder | null {
  const customField = customFieldsById.get(config.customFieldId);
  if (!customField) {
    return null;
  }
  return {
    ...field3ToColumnDef(field2ToField3(customField)),
    colId: getColumnConfigKey(config),
    sortOrder: config.sortOrder ?? Number.MAX_SAFE_INTEGER,
    valueGetter: (params: ValueGetterParams) => {
      return get(params.context.customFieldDataMap, [
        params.data.company.id,
        config.customFieldId,
        getDateForConfig(config),
      ]);
    },
  };
}

// can keep everything local as long as it's called from single component
export function useMprReportData() {
  const viewId = useAtomValue(selectedViewIdPFJ);
  const configs = useAtomValue(savedMprViewConfigAtom(viewId));
  const kpiConfigMap = useAtomValue(kpiConfigByIdMapAtom);
  const kpisByKeyMap = useAtomValue(kpiConfigByKeyMapAtom);
  const columnsByPath = useAtomValue(columnsByPathStateLoadable(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  const customFieldsById = useAtomValue(customFieldsByIdMapLoadable);
  const { pushErrorToast } = useToastMessageState();
  // Requests can finish out of order, so only the newest one may settle the grid or report a failure.
  const latestRequestRef = useRef(0);
  const [inFlight, setInFlight] = useState(false);
  const [settledRequestId, setSettledRequestId] = useState<string>();

  const fundId = useAtomValue(fundFilterStateAtom) || 0;
  const columns = useMemo(() => (configs ? getMprViewColumns(configs) : undefined), [configs]);
  const requestId = getRefetchId(columns, fundId);
  const getReport = useCallback(async () => {
    if (!columns?.length || columnsByPath.state !== 'hasData' || customFieldsById.state !== 'hasData') {
      return;
    }

    const request = ++latestRequestRef.current;
    const isLatest = () => request === latestRequestRef.current;
    setInFlight(true);
    try {
      const payload: IMultiPeriodReportingRequest = {
        dataPoints: columns.reduce((acc, config) => {
          if (!isMprKpiColumnConfig(config)) {
            return acc;
          }
          const pt = mprKpiColumnConfigToDataPoint(config, kpisByKeyMap);
          if (!pt) {
            return acc;
          }
          acc.push(pt);
          return acc;
        }, [] as IMultiPeriodReportingDataPoint[]),
        isDateAdjusted: true,
        metricsDataPoints: mprMetricsColumnConfigsToDataPoints(
          columns.filter(isMprMetricsColumnConfig),
          columnsByPath.data
        ),
        customDataPoints: mprCustomFieldColumnConfigsToDataPoints(
          columns.filter(isMprCustomFieldColumnConfig)
        ),
        fundId,
      };
      return await getMultiPeriodReport(payload);
    } catch (error) {
      if (isLatest()) {
        pushErrorToast({ message: getErrorMessage(error, 'Failed to load report') });
      }
      throw error;
    } finally {
      if (isLatest()) {
        setInFlight(false);
        setSettledRequestId(requestId);
      }
    }
  }, [columnsByPath, customFieldsById, columns, kpisByKeyMap, fundId, requestId, pushErrorToast]);

  const { data } = useAsync(getReport, { id: requestId });

  // With no columns, or config that failed to load, no request is coming, so there is nothing to wait for.
  const canRequest =
    Boolean(columns?.length) && columnsByPath.state !== 'hasError' && customFieldsById.state !== 'hasError';
  const isFetching = canRequest && (inFlight || settledRequestId !== requestId);

  const kpiRowDataMap = useMemo(() => {
    if (!data) {
      return null;
    }
    return getMultiPeriodReportingKpiDataMap(data, kpiConfigMap);
  }, [data, kpiConfigMap]);

  const metricsDataMap = useMemo(() => {
    if (!data || columnsByPath.state !== 'hasData') {
      return null;
    }
    return getMultiPeriodReportingMetricsDataMap(data);
  }, [data, columnsByPath]);

  const customFieldDataMap = useMemo(() => {
    if (!data) {
      return null;
    }
    return getMultiPeriodReportingCustomFieldDataMap(data);
  }, [data]);

  const companyRowData = useMemo(() => {
    return data?.companies.map((company) => {
      return { company };
    });
  }, [data]);

  // one row per company and item in the rows layout, one per company otherwise
  const rowData = useMemo<IMprGridRow[] | undefined>(() => {
    if (!companyRowData || configs?.layout !== MprLayout.rows) {
      return companyRowData;
    }
    const items = uniqueMprItems(configs.items);
    const known =
      columnsByPath.state === 'hasData' && customFieldsById.state === 'hasData'
        ? resolvableMprItems(items, {
            kpiConfigMap: kpisByKeyMap,
            kpiDefinitionsByKey: new Map(),
            columnsByPath: columnsByPath.data,
            customFieldsById: customFieldsById.data,
          })
        : items;
    return companyRowData.flatMap(({ company }) => known.map((item) => ({ company, item })));
  }, [companyRowData, configs, columnsByPath, customFieldsById, kpisByKeyMap]);

  return { isFetching, companyRowData, rowData, kpiRowDataMap, metricsDataMap, customFieldDataMap };
}

function getRefetchId(configs?: MprColumnConfig[] | null, fundId?: number | null) {
  return (
    configs
      ?.map((c) => getColumnConfigKey(c))
      ?.sort()
      .join(',') + (fundId ? `-${fundId}` : '')
  );
}
